import { EnumSchema, FromSchema, OptionalString, requireFields } from '@40seas/entities/common';
import { Address, AddressSchema, Currency } from '@40seas/models';
import { z } from 'zod';
import { AchBankAccountPayload } from './ach.bank-account.payload.entity';
import { AcssBankAccountPayload } from './acss.bank-account.payload.entity';
import { BacsBankAccountPayload } from './bacs.bank-account.payload.entity';
import { BankAccountPayloadSchema } from './bank-account.payload.schema';
import { BecsBankAccountPayload } from './becs.bank-account.payload.entity';
import { CnapsBankAccountPayload } from './cnaps.bank-account.payload.entity';
import { FpsBankAccountPayload } from './fps.bank-account.payload.entity';
import { IbanBankAccountPayload } from './iban.bank-account.payload.entity';
import { IntradagclearingBankAccountPayload } from './intradagclearing.bank-account.payload.entity';
import { MepsBankAccountPayload } from './meps.bank-account.payload.entity';
import { RtgsBankAccountPayload } from './rtgs.bank-account.payload.entity';
import { SgdSwiftBankAccountPayload } from './sgd-swift.bank-account.payload.entity';
import { SpeiBankAccountPayload } from './spei.bank-account.payload.entity';
import { SwiftWithIbanBankAccountPayload } from './swift-with-iban.bank-account.payload.entity';
import { SwiftBankAccountPayload } from './swift.bank-account.payload.entity';
export enum BankAccountDetailsStatus {
    Pending = 'pending',
    Active = 'active',
    Inactive = 'inactive',
}
export enum BankAccountLocation {
    Domestic = 'domestic',
    International = 'international',
}
export const BankAccountDetailsSchema = z.object({
    bankName: z
        .string()
        .regex(/^[a-zA-Z0-9\s.,]{2,140}$/, { message: 'Bank name should contain only letters' })
        .trim()
        .min(1, { message: 'Bank name is required' })
        .optional()
        .transform((value) => value?.replace(/[.,]/g, ''))
        .openapi({ effectType: 'input' }),
    bankAccountName: z
        .string()
        .regex(/^[a-zA-Z0-9\s\u4e00-\u9fa5.,]{2,140}$/, { message: 'Account name should contain only letters' })
        .trim()
        .min(1, { message: 'Bank account name is required' })
        .optional()
        .transform((value) => value?.replace(/[.,]/g, ''))
        .openapi({ effectType: 'input' }),
    email: OptionalString(z.string().email({ message: 'Invalid email address' })),
    accountNumber: z.string().trim().min(1, { message: 'Account number is required' }).optional(),
    routingNumber: OptionalString(z.string().regex(/^\d{10,12}$/, { message: 'Routing number should contain 10 to 12 digits' })),
    swiftCode: OptionalString(z.string().regex(/^\w{8}$|^\w{11}$/, { message: 'Swift code has to contain 8 or 11 letters or digits' })),
    bankAddress: requireFields(AddressSchema, ['country', 'city', 'zipCode', 'addressLine1']).optional(),
    bankAccountPayload: BankAccountPayloadSchema,
    currency: EnumSchema(Currency).optional(),
    status: EnumSchema(BankAccountDetailsStatus).optional(),
    location: EnumSchema(BankAccountLocation).optional(),
    reference: z.string().optional(),
    businessRegistrationNumber: z.string().optional(),
    nickname: z.string().optional(),
});

export class BankAccountDetails extends FromSchema(BankAccountDetailsSchema) {
    bankName?: string;
    bankAccountName?: string;
    email?: string;
    bankAddress?: Address;
    accountNumber?: string;
    routingNumber?: string;
    swiftCode?: string;
    bankAccountPayload: //change to international
    | SwiftBankAccountPayload
        | BecsBankAccountPayload
        | BacsBankAccountPayload
        | AchBankAccountPayload
        | IbanBankAccountPayload
        | AcssBankAccountPayload
        | SpeiBankAccountPayload
        | CnapsBankAccountPayload
        | MepsBankAccountPayload
        | SgdSwiftBankAccountPayload
        | SwiftWithIbanBankAccountPayload
        | FpsBankAccountPayload
        | IntradagclearingBankAccountPayload
        | RtgsBankAccountPayload;
    status?: BankAccountDetailsStatus;
    currency: Currency;
    location?: BankAccountLocation;
    reference?: string;
    businessRegistrationNumber?: string;
    nickname?: string;
}
